import { action } from 'mobx';
import AddressRepository, { IAddressRepositoryConfig, ILoadAddressSchemaOptions } from '../AddressRepository';
import Address, { AddressId, IAddressAttributes } from '../../domain/entities/Address';
import { ADDRESS_TYPE, DeliveryOptions, FIELD_TYPE } from '../../constants/Constants';
import { NOTIFICATION_GROUP } from '../../constants/Notifications';
import TransactionRepository from '../TransactionRepository';
import { CONVENIENCE_STORE } from '../../domain/entities/Config';
import { IAddressSchema } from '../../api/JsonSchemaApi';
import { diContainer, serviceNames } from '../../diContainer/diContainer';

export type IBillingAddressRepositoryConfig = IAddressRepositoryConfig;

class BillingRepository extends AddressRepository {
  constructor(props: IBillingAddressRepositoryConfig) {
    super(props);
    this.addressType = ADDRESS_TYPE.BILLING;
  }

  @action
  public loadAddressSchema = async (
    countryId: number,
    options?: ILoadAddressSchemaOptions,
  ): Promise<IAddressSchema> => {
    const resolvedOptions = {
      ...this.resolveSchemaLoadOptions(),
      ...options,
    };

    return this.fetchAndSetAddressSchema(countryId, resolvedOptions);
  };

  private resolveSchemaLoadOptions(): ILoadAddressSchemaOptions {
    // Lazy resolve to avoid circular DI: transactionRepository also depends on billingRepository.
    const transactionRepository: TransactionRepository = diContainer.get(serviceNames.transactionRepository);
    const { transaction } = transactionRepository;
    const isConvenienceStorePickUp = transaction?.deliveryOption === DeliveryOptions.CONVENIENCE_STORE_PICK_UP;
    const convenienceStoreType = isConvenienceStorePickUp
      ? transaction?.convenienceStore?.storeType as CONVENIENCE_STORE | undefined
      : undefined;

    return { convenienceStoreType };
  }

  @action
  public async saveNewAddress(address: Address): Promise<Address> {
    try {
      const addressData = Address.toJSON(address);

      addressData[FIELD_TYPE.ZIPCODE] = addressData[FIELD_TYPE.ZIPCODE].toString().toUpperCase();

      const response: { address?: IAddressAttributes } = await this.api.createBillingAddress(addressData);

      const newAddress = Address.fromJSON(response.address);
      this.addAddress(newAddress);

      return newAddress;
    } catch (error) {
      this.handleAddressError(error, NOTIFICATION_GROUP.BILLING);
    }
  }

  @action
  public selectAddress = async (id: AddressId, options?: { signal?: AbortSignal }): Promise<void> => {
    this.notificationsCollection.clearNotifications(NOTIFICATION_GROUP.BILLING);
    try {
      await this.api.selectBillingAddress(id, { signal: options?.signal });
      if (this.config.jsonSchemasEnabled) {
        const address = this.getAddressById(id);
        await this.loadAddressSchema(address.attributes[FIELD_TYPE.COUNTRY_ID]);
      }
    } catch (error) {
      this.handleAddressError(error, NOTIFICATION_GROUP.BILLING, options?.signal);
    }
  };
}

export default BillingRepository;
